import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import { CompRef } from '@wix/document-services-types';
import {
  CssCustomProperties,
  CssPropertyItem,
} from '@wix/component-protocol/schema';
import type { EditorPointer } from '@wix/editor-document-logic-api';

const NONE = 'none';

export class DisplayVisibilityPlatformService extends AbstractComponentsPlatformService {
  isVisibleElementOnStage(editorPointer: EditorPointer<CompRef>, key?: string) {
    const styles = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getAPI()
      .query.getStyles();

    const { cssCustomProperties, cssProperties } = styles;

    if (key) {
      return cssCustomProperties[key] === 'block';
    }

    return cssProperties.display !== 'none';
  }

  #getVisibleValue(
    value: CssCustomProperties[string] | CssPropertyItem,
    visibleValue?: string,
  ) {
    const defaultValue = value.defaultValue;
    const isDesiredVisibleValueValid =
      value.display?.displayValues?.find((x) => x === visibleValue) &&
      visibleValue !== NONE &&
      visibleValue;

    if (isDesiredVisibleValueValid) {
      return visibleValue;
    }

    return (
      (defaultValue === NONE || !defaultValue
        ? value.display?.displayValues?.find((x) => x !== NONE)
        : defaultValue) ?? 'block'
    );
  }

  showElementOnStage(
    editorPointer: EditorPointer<CompRef>,
    key?: string,
    value?: string,
  ) {
    const context = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getAPI();

    const styleDefinitions = context.resolver.getStyleDefinitions();

    const cssDisplayDefinition = key
      ? styleDefinitions.cssCustomProperties[key]
      : styleDefinitions.cssProperties.display;

    if (!cssDisplayDefinition) {
      return;
    }

    const shouldRemoveStyle =
      cssDisplayDefinition.display?.displayValues?.length === 1 &&
      cssDisplayDefinition.display?.displayValues[0] === NONE;

    if (shouldRemoveStyle) {
      context.query.removeStyles(
        key
          ? { cssCustomPropertiesKeys: [key] }
          : { cssPropertiesKeys: ['display'] },
      );

      return;
    }

    const visibleValue = this.#getVisibleValue(cssDisplayDefinition, value);

    context.query.setStyles(
      key
        ? { cssCustomProperties: { [key]: visibleValue } }
        : { cssProperties: { display: visibleValue } },
    );
  }

  hideElementOnStage(editorPointer: EditorPointer<CompRef>, key?: string) {
    const context = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getAPI();

    context.query.setStyles(
      key
        ? { cssCustomProperties: { [key]: 'none' } }
        : { cssProperties: { display: 'none' } },
    );
  }
}
